/* GASSISA
=========================================================================================
 * Descripción: Modal para aplicar ofertas a artículos en Centro de Costos
 * Historial de cambios:
 * ---------------------------------------------------------------------------------------
 *    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
 * ---------------------------------------------------------------------------------------
 *      1       | 13/05/2026 | Lic. José Luis Aragón Cervantes  | Creación módulo ofertas
 *      2       | 13/05/2026 | Lic. José Luis Aragón Cervantes  | Agregar botón eliminar por fechas
 * --------------------------------------------------------------------------------------- */

import TextField from "../ui/TextField";
import { useState } from "react";

import { Box, Button, Typography, Grid, Alert, Divider } from "@mui/material";

import Modal from "../utils/Modal";

export default function ModalAplicarOferta(props) {

    const {
        open,
        onClose,
        onAplicar,
        onEliminarPorFechas,
        articulosOferta = [],
        loading,
        totalListaOriginal = 0
    } = props;

    const cantidadArticulos = articulosOferta.length;

    const usandoFiltros =
        cantidadArticulos > 0 &&
        cantidadArticulos < totalListaOriginal;

    const [fechaInicio, setFechaInicio] = useState(
        new Date().toISOString().split("T")[0]
    );

    const [fechaFin, setFechaFin] = useState(() => {

        const fecha = new Date();

        fecha.setMonth(fecha.getMonth() + 1);

        return fecha.toISOString().split("T")[0];
    });

    const [costoOferta, setCostoOferta] = useState("");

    const [cantidadAplicable, setCantidadAplicable] = useState(1);

    const [error, setError] = useState("");

    const validarFechas = () => {

        if (!fechaInicio || !fechaFin) {

            setError(
                "Las fechas de inicio y fin son obligatorias"
            );

            return null;
        }

        const inicio = new Date(fechaInicio);

        const fin = new Date(fechaFin);

        if (inicio > fin) {

            setError(
                "La fecha de fin debe ser mayor a la fecha de inicio"
            );

            return null;
        }

        return {
            inicio,
            fin
        };
    };

    const handleAplicar = async () => {

        setError("");

        if (cantidadArticulos === 0) {

            setError(
                "No hay artículos para aplicar oferta"
            );

            return;
        }

        const fechas = validarFechas();

        if (!fechas) return;

        const costo = parseFloat(costoOferta);

        if (isNaN(costo) || costo < 0) {

            setError(
                "El precio de oferta debe ser un número vélido"
            );

            return;
        }

        const cantidad = parseInt(cantidadAplicable, 10);

        if (isNaN(cantidad) || cantidad < 1) {

            setError(
                "La cantidad aplicable debe ser un número vélido mayor a 0"
            );

            return;
        }

        await onAplicar({
            dtFechaInicio: fechas.inicio,
            dtFechaFin: fechas.fin,
            dCostoOferta: costo,
            dCantidadAplicable: cantidad
        });
    };

    const handleEliminarPorFechasLocal = async () => {

        setError("");

        const fechas = validarFechas();

        if (!fechas) return;

        await onEliminarPorFechas({
            dtFechaInicio: fechas.inicio,
            dtFechaFin: fechas.fin
        });
    };

    const handleClose = () => {

        setError("");

        setCostoOferta("");

        setCantidadAplicable(1);

        onClose();
    };

    return (

        <Modal
            open={open}
            onClose={handleClose}
            onSuccess={handleAplicar}
            title="Gestión de Ofertas"
            success="Aplicar Oferta"
            cancel="Cerrar"
            maxWidth="sm"
            disableSuccess={
                loading ||
                cantidadArticulos === 0
            }
        >

            <Box sx={{ p: 2 }}>

                <Grid container spacing={2}>

                    <Grid item xs={12}>

                        <Typography
                            variant="h6"
                            gutterBottom
                        >
                            Aplicar Oferta
                        </Typography>

                        <Typography
                            variant="body2"
                            color="textSecondary"
                        >

                            Se aplicaré la oferta a{" "}

                            <strong>
                                {cantidadArticulos}
                            </strong>

                            {" "}artículo(s)

                            {usandoFiltros
                                ? " visibles según los filtros actuales."
                                : " de la lista completa."
                            }

                        </Typography>

                    </Grid>

                    <Grid item xs={6}>

                        <TextField autoComplete="off"
                            label="Fecha Inicio"
                            type="date"
                            value={fechaInicio}
                            onChange={(e) =>
                                setFechaInicio(
                                    e.target.value
                                )
                            }
                            InputLabelProps={{
                                shrink: true
                            }}
                            fullWidth
                            size="small"
                        />

                    </Grid>

                    <Grid item xs={6}>

                        <TextField autoComplete="off"
                            label="Fecha Fin"
                            type="date"
                            value={fechaFin}
                            onChange={(e) =>
                                setFechaFin(
                                    e.target.value
                                )
                            }
                            InputLabelProps={{
                                shrink: true
                            }}
                            fullWidth
                            size="small"
                        />

                    </Grid>

                    <Grid item xs={6}>

                        <TextField autoComplete="off"
                            label="Precio de Oferta"
                            type="text"
                            inputMode="numeric"
                            enterKeyHint="enter"
                            value={costoOferta}
                            onChange={(e) =>
                                setCostoOferta(
                                    e.target.value
                                )
                            }
                            fullWidth
                            size="small"
                            inputProps={{
                                min: 0,
                                step: 0.01
                            }}
                        />

                    </Grid>

                    <Grid item xs={6}>

                        <TextField autoComplete="off"
                            label="Cantidad Aplicable"
                            type="text"
                            inputMode="numeric"
                            enterKeyHint="enter"
                            value={cantidadAplicable}
                            onChange={(e) =>
                                setCantidadAplicable(
                                    parseInt(
                                        e.target
                                            .value,
                                        10
                                    ) || 1
                                )
                            }
                            fullWidth
                            size="small"
                            inputProps={{
                                min: 1,
                                step: 1
                            }}
                        />

                    </Grid>

                    <Grid item xs={12}>

                        <Divider sx={{ my: 2 }} />

                    </Grid>

                    <Grid item xs={12}>

                        <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            onClick={
                                handleEliminarPorFechasLocal
                            }
                            disabled={loading}
                        >

                            Eliminar ofertas por fechas

                        </Button>

                    </Grid>

                    {error && (

                        <Grid item xs={12}>

                            <Alert severity="error">

                                {error}

                            </Alert>

                        </Grid>

                    )}

                </Grid>

            </Box>

        </Modal>
    );
}